/**
 * Copyright Highway9 Networks Inc.
 */

import {
  PayloadAction,
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  type EntityState,
} from "@reduxjs/toolkit";
import type { RootState } from "..";
import memoryService from "~/services/memory-service";
import moment from "moment-timezone";
import { ChatMemory } from "~/types/chatMemory";

export const memoriesAdapter = createEntityAdapter<ChatMemory>({
  sortComparer: (a, b) => b.creationTime - a.creationTime,
});

export interface MemoryState {
  memoriesData: EntityState<ChatMemory>;
  searchTerm: string;
  categoryFilter: string; // "ALL" | "RADIO" | "EDGE" | "DEVICE"
  loading: boolean;
  error: string | null;
  expandedMemoryIds: string[];
  editingMemoryId: string | null;
}

const initialState: MemoryState = {
  memoriesData: memoriesAdapter.getInitialState(),
  searchTerm: "",
  categoryFilter: "ALL",
  loading: false,
  error: null,
  expandedMemoryIds: [],
  editingMemoryId: null,
};

// Async thunks for API operations
export const fetchMemories = createAsyncThunk(
  "memory/fetchMemories",
  async (_, { rejectWithValue }) => {
    try {
      return await memoryService.fetchMemories();
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch memories");
    }
  },
);

export const createMemory = createAsyncThunk(
  "memory/createMemory",
  async (
    memory: Omit<ChatMemory, "id" | "creationTime" | "updatedTime" | "owner">,
    { rejectWithValue },
  ) => {
    try {
      return await memoryService.createMemory(memory as ChatMemory);
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to create memory");
    }
  },
);

export const updateMemory = createAsyncThunk(
  "memory/updateMemory",
  async (
    { id, updates }: { id: string; updates: Partial<ChatMemory> },
    { rejectWithValue },
  ) => {
    try {
      return await memoryService.updateMemory(updates as ChatMemory, id);
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to update memory");
    }
  },
);

export const deleteMemory = createAsyncThunk(
  "memory/deleteMemory",
  async (id: string, { rejectWithValue }) => {
    try {
      await memoryService.deleteMemory(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to delete memory");
    }
  },
);

const memorySlice = createSlice({
  name: "memory",
  initialState,
  reducers: {
    setSearchTerm: (state, action: PayloadAction<string>) => {
      state.searchTerm = action.payload;
    },
    setCategoryFilter: (state, action: PayloadAction<string>) => {
      state.categoryFilter = action.payload;
    },
    clearFilters: (state) => {
      state.searchTerm = "";
      state.categoryFilter = "ALL";
    },
    toggleMemoryExpanded: (state, action: PayloadAction<string>) => {
      const memoryId = action.payload;
      const index = state.expandedMemoryIds.indexOf(memoryId);
      if (index >= 0) {
        state.expandedMemoryIds.splice(index, 1);
      } else {
        state.expandedMemoryIds.push(memoryId);
      }
    },
    /** Store editing identity only; resolve the entity via selectors. */
    setEditingMemory: (state, action: PayloadAction<string | null>) => {
      state.editingMemoryId = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    // Optimistic update for better UX
    optimisticUpdateMemory: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<ChatMemory> }>,
    ) => {
      const { id, updates } = action.payload;
      memoriesAdapter.updateOne(state.memoriesData, {
        id,
        changes: {
          ...updates,
          updatedTime: moment().unix(),
        },
      });
    },
    // Optimistic delete for better UX
    optimisticDeleteMemory: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      memoriesAdapter.removeOne(state.memoriesData, id);
      state.expandedMemoryIds = state.expandedMemoryIds.filter(
        (memoryId) => memoryId !== id,
      );
      if (state.editingMemoryId === id) {
        state.editingMemoryId = null;
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch memories
    builder
      .addCase(fetchMemories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMemories.fulfilled, (state, action) => {
        state.loading = false;
        memoriesAdapter.setAll(state.memoriesData, action.payload);
      })
      .addCase(fetchMemories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create memory
    builder
      .addCase(createMemory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createMemory.fulfilled, (state, action) => {
        state.loading = false;
        memoriesAdapter.addOne(state.memoriesData, action.payload);
      })
      .addCase(createMemory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update memory
    builder
      .addCase(updateMemory.pending, (state) => {
        state.error = null;
      })
      .addCase(updateMemory.fulfilled, (state, action) => {
        memoriesAdapter.upsertOne(state.memoriesData, action.payload);
        state.editingMemoryId = null;
      })
      .addCase(updateMemory.rejected, (state, action) => {
        state.error = action.payload as string;
        // Revert optimistic update if needed
        // This would require storing the original state, which we'll handle in the component
      });

    // Delete memory
    builder
      .addCase(deleteMemory.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteMemory.fulfilled, (state, action) => {
        // Ensure it's removed (in case optimistic update failed)
        memoriesAdapter.removeOne(state.memoriesData, action.payload);
      })
      .addCase(deleteMemory.rejected, (state, action) => {
        state.error = action.payload as string;
        // For delete failures, we should restore the memory
        // This would require storing the deleted memory, which we'll handle in the component
      });
  },
});

export const {
  setSearchTerm,
  setCategoryFilter,
  clearFilters,
  toggleMemoryExpanded,
  setEditingMemory,
  clearError,
  optimisticUpdateMemory,
  optimisticDeleteMemory,
} = memorySlice.actions;

export const memoryActions = memorySlice.actions;
export default memorySlice.reducer;

const memoryEntitySelectors = memoriesAdapter.getSelectors(
  (state: RootState) => state.memory.memoriesData,
);

export const memoryData = memoryEntitySelectors.selectAll;
export const memoryEntities = memoryEntitySelectors.selectEntities;

export const memorySearchTerm = (state: RootState) => state.memory.searchTerm;
export const memoryCategoryFilter = (state: RootState) =>
  state.memory.categoryFilter;
export const memoryLoading = (state: RootState) => state.memory.loading;
export const memoryError = (state: RootState) => state.memory.error;
export const memoryExpandedIds = (state: RootState) =>
  state.memory.expandedMemoryIds;
export const memoryEditingId = (state: RootState) =>
  state.memory.editingMemoryId;

/** Filtered + sorted list derived from entity collection and UI filters. */
export const memoryFiltered = createSelector(
  [memoryData, memorySearchTerm, memoryCategoryFilter],
  (memories, searchTerm, categoryFilter) => {
    let filtered = memories;

    if (categoryFilter !== "ALL") {
      filtered = filtered.filter((memory) => memory.category === categoryFilter);
    }

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        (memory) =>
          memory.title.toLowerCase().includes(searchLower) ||
          memory.content.toLowerCase().includes(searchLower),
      );
    }

    return filtered;
  },
);

export const selectIsMemoryExpanded =
  (memoryId: string) => (state: RootState) =>
    state.memory.expandedMemoryIds.includes(memoryId);

export const selectIsMemoryEditing =
  (memoryId: string) => (state: RootState) =>
    state.memory.editingMemoryId === memoryId;

export const selectMemoryById =
  (memoryId: string) => (state: RootState) =>
    memoryEntities(state)[memoryId];

export const selectEditingMemory = createSelector(
  [memoryEditingId, memoryEntities],
  (id, entities) => (id ? entities[id] ?? null : null),
);

export const selectMemoryStatistics = createSelector(
  [memoryData, memoryFiltered],
  (memories, filtered) => ({
    total: memories.length,
    byCategory: {
      RADIO: memories.filter((m) => m.category === "RADIO").length,
      EDGE: memories.filter((m) => m.category === "EDGE").length,
      DEVICE: memories.filter((m) => m.category === "DEVICE").length,
    },
    filtered: filtered.length,
  }),
);
